import { type FC, type MutableRefObject, type ReactNode, useState } from 'react'
import dayjs from 'dayjs'
import { core } from '@musinsa/pbo-core'
import { Icon as DsIcon } from '@musinsa/pbo-assets'
import { BULK_INPUT_TOAST_SECONDS, DATE_FORMAT } from '@/utils/consts'

/** core.Table apiRef 에 채워지는 표 API — pbo-core 가 타입을 따로 내보내지 않아 apiRef prop 에서 꺼낸다. */
export type TableApi<T = Record<string, unknown>> = NonNullable<NonNullable<core.CoreTableProps<T>['apiRef']>['current']>

type CellOption = { value: string; label: string }

export interface BulkInputItem {
  /** 표 컬럼 dataIndex — 입력 칸은 이 컬럼 variant 를 따른다(select → variantProps.options · datepicker · 그 외 input). */
  key: string
  label: string
  /** select 선택지 — 컬럼 variantProps.options 대신 쓴다(예: RGR16 화주코드). 주면 칸은 select 다. */
  options?: CellOption[]
  /** 입력 값 → 행 패치. 기본 `{ [key]: value }`. 한 값으로 여러 칸을 채울 때(화주코드 → 화주명·서비스유형). */
  toPatch?: (value: string) => Record<string, unknown>
}

interface BulkInputMenuProps<T> {
  apiRef: MutableRefObject<TableApi<T> | null>
  columns: core.CoreColumnType<T>[]
  items: BulkInputItem[]
  /** 표 rowKey 필드 — 선택 행을 표 행으로 되찾는 키. 기본 'key'. */
  rowKey?: string
  /** [저장] 이 없는 화면(RGR17·RGR18)은 false — 토스트에서 저장 안내 문장을 뺀다. */
  saveHint?: boolean
  /** 앞에 행 동작 버튼([삭제] 등)이 없으면 false — 구분선을 그리지 않는다(RGR17). */
  divider?: boolean
}

// 비활성 버튼은 마우스 이벤트를 받지 않아 span 으로 감싸야 툴팁이 뜬다(mwms-fe #1619 gateFooter 와 같은 방식)
const gateFooter =
  (tip: string) =>
  (_: ReactNode, { OkBtn, CancelBtn }: { OkBtn: FC; CancelBtn: FC }) => (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, alignItems: 'center' }}>
      <CancelBtn />
      <core.Tooltip title={tip || undefined}>
        <span style={{ display: 'inline-flex' }}>
          <OkBtn />
        </span>
      </core.Tooltip>
    </div>
  )

/**
 * 표 [일괄 입력 ⌄] 메뉴 + 항목별 모달(pbo-design #822 우편번호권역 일괄 입력 UX).
 * [반영] = 체크한 행마다 apiRef.patchCell — 셀 편집과 같은 onRowsChange 를 타서 기존 [저장] 으로 저장된다. API 호출 없음·체크 유지.
 * 같은 core.TableSelectionScope 안에 둬야 선택 행을 읽는다.
 *
 * ⚠️ patchCell 은 표가 지금 들고 있는 행(현재 페이지)에서만 rowKey 를 찾는다. 페이지 이동은 체크를 비우므로 체크 행은 늘 현재 페이지지만,
 *    표 밖 행에는 쓰지 않는다(기본 페이지 크기 10,000).
 */
function BulkInputMenu<T>({ apiRef, columns, items, rowKey = 'key', saveHint = true, divider = true }: BulkInputMenuProps<T>) {
  const selection = core.useTableSelection()
  // 항목은 key 로만 들고 렌더마다 items 에서 찾는다 — 연 뒤 배선이 바뀐 toPatch(예: 늦게 온 센터 목록)를 쓰게
  const [itemKey, setItemKey] = useState<string | null>(null)
  const item = items.find((it) => it.key === itemKey) ?? null
  // null = 아직 안 고름. '' 도 고른 값이다 — 선택지 value 가 '' 인 것(RGR16 거래유형 '온라인'·불량구분 '양품')이 있다
  const [value, setValue] = useState<string | null>(null)
  const selected = selection?.rows ?? []

  const column = item ? columns.find((c) => 'dataIndex' in c && c.dataIndex === item.key) : undefined
  const options = item?.options ?? (column?.variant === 'select' ? column.variantProps?.options : undefined)

  const keyOf = (r: Record<string, unknown>): string => {
    const k = r[rowKey]
    return typeof k === 'string' || typeof k === 'number' ? String(k) : ''
  }
  const close = () => setItemKey(null)
  const apply = () => {
    const api = apiRef.current
    if (!item || !api) return
    const tableKeys = new Set(api.rows.map((r) => keyOf(r as Record<string, unknown>)))
    const keys = selected.map(keyOf).filter((k) => tableKeys.has(k))
    if (value === null) return
    // 공백 정리는 입력 칸만 — 선택지·날짜 값은 그대로
    const text = options || column?.variant === 'datepicker' ? value : value.trim()
    const patch = item.toPatch ? item.toPatch(text) : { [item.key]: text }
    keys.forEach((k) => api.patchCell(k, patch))
    core.message.success(`${item.label} 값을 ${keys.length}건에 입력했습니다.${saveHint ? ' 저장하려면 [저장]을 누르세요.' : ''}`, BULK_INPUT_TOAST_SECONDS)
    close()
  }

  const renderInput = () => {
    if (options) {
      return <core.Select showSearch optionFilterProp="label" options={options} value={value ?? undefined} onChange={(v: string) => setValue(v)} placeholder="선택하세요." style={{ width: '100%' }} />
    }
    if (column?.variant === 'datepicker') {
      return <core.DatePicker placeholder="선택하세요." value={value ? dayjs(value) : null} onChange={(d) => setValue(d ? d.format(DATE_FORMAT.date) : null)} style={{ width: '100%' }} />
    }
    return <core.Input value={value ?? ''} onChange={(e) => setValue(e.target.value)} placeholder="입력하세요." />
  }

  const filled = value !== null && (options ? true : value.trim() !== '')
  const tip = filled ? '' : '값을 입력하세요.'
  return (
    // core.Space 는 이 부품을 한 칸에 담는다 — 구분선(block)·버튼이 세로로 쌓이지 않게 한 줄 가운데 정렬
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      {/* [저장]·[삭제](행 확정·제거) ↔ [일괄 입력](값 채우기) 구분 */}
      {divider && <span style={{ height: 18, margin: '0 4px', borderLeft: '1px solid #d9d9d9', display: 'block', flex: 'none' }} />}
      <core.Dropdown
        trigger={['click']}
        disabled={!selected.length}
        menu={{
          items: items.map((it) => ({ key: it.key, label: it.label })),
          onClick: ({ key }) => {
            setValue(null)
            setItemKey(key)
          },
        }}
      >
        <core.Button disabled={!selected.length} icon={<DsIcon name="ic-chevronDown" size={16} color="currentColor" />} iconPosition="end">
          일괄 입력
        </core.Button>
      </core.Dropdown>
      <core.Modal
        destroyOnHidden
        open={!!item}
        title={`${item?.label ?? ''} 일괄 입력`}
        width={420}
        okText="반영"
        cancelText="취소"
        okButtonProps={{ disabled: !!tip }}
        footer={gateFooter(tip)}
        onOk={apply}
        onCancel={close}
      >
        <core.FormItem label={item?.label} labelPosition="top" boxedItem={false}>
          {renderInput()}
        </core.FormItem>
      </core.Modal>
    </span>
  )
}

export default BulkInputMenu
